import { useState } from 'react'
import { FiAlertCircle, FiLock, FiUser, FiX } from 'react-icons/fi'
import { completeEmailOnboarding } from '../services/api'
import { useAuthSession } from '../contexts/AuthSessionContext'

export default function EmailOnboardingModal() {
  const { user, onboardingToken, setAuthSession, logout } = useAuthSession()
  const [name, setName] = useState(() => String(user?.name || '').trim())
  const [hasCryptoWallet, setHasCryptoWallet] = useState(() => (user?.wallet ? true : null))
  const [cryptoWallet, setCryptoWallet] = useState(() => String(user?.wallet || ''))
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  if (!onboardingToken) return null

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    const nameTrim = name.trim()
    if (!nameTrim) return setError('Please enter your full name')
    if (hasCryptoWallet === null) {
      return setError('Please tell us if you already have a crypto wallet')
    }
    if (hasCryptoWallet) {
      const w = cryptoWallet.trim()
      if (!w) return setError('Please enter your crypto wallet address')
      if (!/^0x[a-fA-F0-9]{40}$/.test(w)) {
        return setError('Please enter a valid wallet address (0x...)')
      }
    }
    if (password.length < 8) return setError('Password must be at least 8 characters')
    if (password !== confirmPassword) return setError('Passwords do not match')

    setSubmitting(true)
    try {
      const res = await completeEmailOnboarding(onboardingToken, {
        name: nameTrim,
        password,
        hasCryptoWallet,
        cryptoWallet: hasCryptoWallet ? cryptoWallet.trim() : undefined,
      })
      const d = res.data
      setAuthSession({
        user: d.user,
        emailAuthToken: d.emailAuthToken,
        onboardingToken: null,
      })
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Could not complete setup'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/80 p-4 pt-16 sm:pt-24">
      <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-900 p-6 relative shadow-2xl">
        <div className="absolute top-4 right-4 text-gray-500" aria-hidden>
          <FiX size={20} className="opacity-40" />
        </div>

        <h2 className="text-2xl font-bold text-white">Complete your account</h2>
        <p className="text-gray-400 text-sm mt-1">
          Set your name, optional wallet, and a password to secure your M1NT owner account.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Full name</label>
            <div className="relative">
              <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                autoComplete="name"
                required
              />
            </div>
          </div>

          <div>
            <p className="block text-sm text-gray-300 mb-2">Do you already have a crypto wallet?</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`rounded-lg py-2 px-3 border text-sm ${
                  hasCryptoWallet === true
                    ? 'border-cyan-400 text-cyan-300 bg-cyan-500/10'
                    : 'border-gray-700 text-gray-300 bg-gray-800/80'
                }`}
                onClick={() => setHasCryptoWallet(true)}
                disabled={submitting}
              >
                Yes
              </button>
              <button
                type="button"
                className={`rounded-lg py-2 px-3 border text-sm ${
                  hasCryptoWallet === false
                    ? 'border-cyan-400 text-cyan-300 bg-cyan-500/10'
                    : 'border-gray-700 text-gray-300 bg-gray-800/80'
                }`}
                onClick={() => setHasCryptoWallet(false)}
                disabled={submitting}
              >
                No
              </button>
            </div>
          </div>

          {hasCryptoWallet === true && (
            <div>
              <label className="block text-sm text-gray-300 mb-2">Wallet address</label>
              <input
                type="text"
                className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white font-mono text-sm"
                placeholder="0x..."
                value={cryptoWallet}
                onChange={(e) => setCryptoWallet(e.target.value)}
                disabled={submitting}
                autoComplete="off"
              />
            </div>
          )}

          {hasCryptoWallet === false && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="text-xs text-amber-100 leading-relaxed flex items-start gap-2">
                <FiAlertCircle className="mt-0.5 shrink-0" />
                You can add a wallet later in your profile. To sign ownership declarations and use
                on-chain features, you will need to connect a wallet in the app.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-300 mb-2">Create password</label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="password"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                autoComplete="new-password"
                minLength={8}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-2">Confirm password</label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="password"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                placeholder="Repeat password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                autoComplete="new-password"
                minLength={8}
              />
            </div>
          </div>

          {error ? <p className="text-sm text-red-400">{error}</p> : null}

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Saving…' : 'Save and continue'}
          </button>

          <button
            type="button"
            onClick={() => logout()}
            disabled={submitting}
            className="w-full text-sm text-gray-500 hover:text-gray-300 py-2"
          >
            Sign out
          </button>
        </form>
      </div>
    </div>
  )
}
